import { useEffect, useRef } from "react";
import { useAuthStore } from "../stores/authStore";
import apiClient from "../core/apiClient";

// אחרי כמה זמן בלי פעילות (או זמן שינה של המחשב) נבדוק מיד את הסשן ברגע שהמשתמש חוזר
const RETURN_CHECK_THRESHOLD_MS = 5 * 60 * 1000;
// מרווח מינימלי בין שתי בדיקות מיידיות (focus + visibilitychange נורים יחד)
const MIN_CHECK_GAP_MS = 30 * 1000;

/**
 * Hook ל-Keep-Alive - שומר על החיבור חי על ידי שליחת פינגים לשרת
 * עובד טוב עם מספר טאבים - רק טאבים פעילים שולחים keep-alive
 *
 * בנוסף: כשהמשתמש חוזר לטאב (focus / visibilitychange / פעילות אחרי הפסקה ארוכה / יציאה מ-sleep)
 * נשלחת בדיקה מיידית לשרת. אם הסשן פג - ה-401 interceptor מפנה ל-login.
 *
 * @param intervalMs - מרווח זמן בין פינגים במילישניות (ברירת מחדל: 5 דקות)
 */
export const useKeepAlive = (intervalMs: number = 5 * 60 * 1000) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const lastActivityRef = useRef<number>(Date.now());
  const lastCheckRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // אם לא מחובר, לא צריך keep-alive
      return;
    }

    // פונקציה לשליחת keep-alive לשרת
    // השרת מרענן את הטוקן אם צריך; אם פג - 401 יטופל על ידי interceptor
    const pingServer = async () => {
      lastCheckRef.current = Date.now();
      try {
        await apiClient.get("/auth/keep-alive");
      } catch (error) {
        console.error("Keep-alive failed:", error);
      }
    };

    // בדיקה מיידית כשהמשתמש חוזר - עם הגנה מפני כפילויות
    const checkOnReturn = () => {
      if (Date.now() - lastCheckRef.current < MIN_CHECK_GAP_MS) return;
      pingServer();
    };

    // אירועים שמעידים על פעילות
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Throttle - עדכון לא יותר מפעם ב-10 שניות
    let throttleTimeout: NodeJS.Timeout | null = null;

    const handleActivity = () => {
      if (!throttleTimeout) {
        const idleMs = Date.now() - lastActivityRef.current;
        lastActivityRef.current = Date.now();
        // פעילות ראשונה אחרי הפסקה ארוכה - בודקים מיד שהסשן עדיין תקף
        if (idleMs > RETURN_CHECK_THRESHOLD_MS) {
          checkOnReturn();
        }
        throttleTimeout = setTimeout(() => {
          throttleTimeout = null;
        }, 10000); // 10 שניות
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkOnReturn();
      }
    };

    // הוספת event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });
    window.addEventListener("focus", checkOnReturn);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    let lastTick = Date.now();

    const onInterval = () => {
      const now = Date.now();
      // ה-interval קפא (sleep / טאב מושהה) - בודקים מיד
      const wasSuspended = now - lastTick > intervalMs + RETURN_CHECK_THRESHOLD_MS;
      lastTick = now;

      const timeSinceLastActivity = now - lastActivityRef.current;

      // שולח keep-alive רק אם היתה פעילות בדקות האחרונות
      // זה מונע מטאבים לא פעילים לשלוח keep-alive
      if (wasSuspended || timeSinceLastActivity < intervalMs) {
        pingServer();
      }
    };

    // התחלת interval לשליחת keep-alive
    // שולח כל שעה במקום כל 5 דקות - חוסך בקשות
    // השרת מרענן רק אחרי 90 דקות ממילא
    intervalRef.current = setInterval(onInterval, intervalMs);

    // ניקוי
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener("focus", checkOnReturn);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [isAuthenticated, intervalMs]);

  return {
    lastActivity: lastActivityRef.current,
  };
};
